import React, { useState, useEffect } from "react";
import axios from "axios";
import { AmplifyLogo } from "@/components/AmplifyLogo";
import { Eye, EyeOff, Loader2, CheckCircle, XCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ResetPasswordForm = ({ token, onSwitchToLogin }) => {
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isVerifying, setIsVerifying] = useState(true);
  const [error, setError] = useState("");
  const [tokenValid, setTokenValid] = useState(false);
  const [success, setSuccess] = useState(false);

  // Verify token on mount
  useEffect(() => {
    const verifyToken = async () => {
      try {
        await axios.get(`${API}/auth/verify-reset-token/${token}`);
        setTokenValid(true);
      } catch (err) {
        setError(err.response?.data?.detail || "Invalid or expired reset link");
        setTokenValid(false);
      } finally {
        setIsVerifying(false);
      }
    };

    if (token) {
      verifyToken();
    } else {
      setIsVerifying(false);
      setError("No reset token provided");
    }
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (password !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    if (password.length < 6) {
      setError("Password must be at least 6 characters");
      return;
    }

    setIsLoading(true);

    try {
      await axios.post(`${API}/auth/reset-password`, {
        token,
        new_password: password,
      });
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to reset password");
    } finally {
      setIsLoading(false);
    }
  };

  // Loading state
  if (isVerifying) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4">
        <div className="text-center">
          <AmplifyLogo size={48} className="mx-auto mb-4" />
          <Loader2 className="animate-spin mx-auto text-[#3c68ff] mb-3" size={28} />
          <p className="text-sm text-slate-500 font-medium">Verifying reset link...</p>
        </div>
      </div>
    );
  }

  // Invalid token
  if (!tokenValid && !success) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4" data-testid="reset-password-invalid">
        <div className="w-full max-w-md">
          <div className="bg-white rounded-2xl shadow-xl border border-slate-200 p-8 text-center">
            <div className="flex items-center justify-center w-16 h-16 bg-red-100 rounded-full mx-auto mb-4">
              <XCircle size={32} className="text-red-600" />
            </div>
            <h1 className="text-xl font-semibold text-slate-800 mb-2">Invalid Reset Link</h1>
            <p className="text-sm text-slate-500 mb-6">{error || "This password reset link is invalid or has expired."}</p>
            <button
              onClick={onSwitchToLogin}
              className="w-full h-11 bg-[#3c68ff] text-white rounded-lg text-sm font-semibold hover:bg-[#2b4bca] transition-all"
            >
              Back to Sign In
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Success state
  if (success) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4" data-testid="reset-password-success">
        <div className="w-full max-w-md">
          <div className="bg-white rounded-2xl shadow-xl border border-slate-200 p-8 text-center">
            <div className="flex items-center justify-center w-16 h-16 bg-emerald-100 rounded-full mx-auto mb-4">
              <CheckCircle size={32} className="text-emerald-600" />
            </div>
            <h1 className="text-xl font-semibold text-slate-800 mb-2">Password Reset!</h1>
            <p className="text-sm text-slate-500 mb-6">Your password has been successfully updated. You can now sign in with your new password.</p>
            <button
              onClick={onSwitchToLogin}
              className="w-full h-11 bg-[#3c68ff] text-white rounded-lg text-sm font-semibold hover:bg-[#2b4bca] transition-all"
            >
              Sign In
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Reset form
  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4" data-testid="reset-password-page">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-2xl shadow-xl border border-slate-200 p-8">
          {/* Logo & Header */}
          <div className="text-center mb-8">
            <div className="flex items-center justify-center gap-3 mb-4">
              <AmplifyLogo size={40} />
              <span className="text-2xl font-semibold font-['Outfit'] text-slate-900 tracking-tight">Amplify HR</span>
            </div>
            <h1 className="text-xl font-semibold text-slate-800">Reset Your Password</h1>
            <p className="text-sm text-slate-500 mt-1">Enter your new password below</p>
          </div>

          {/* Error Message */}
          {error && (
            <div className="mb-6 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600" data-testid="reset-password-error">
              {error}
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">New Password</label>
              <div className="relative">
                <input
                  data-testid="reset-password-input"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter new password"
                  required
                  minLength={6}
                  className="w-full h-11 rounded-lg border border-slate-200 bg-white px-4 pr-10 text-sm focus:outline-none focus:ring-2 focus:ring-[#3c68ff] focus:ring-offset-1"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Confirm Password</label>
              <input
                data-testid="reset-password-confirm-input"
                type={showPassword ? "text" : "password"}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm new password"
                required
                className="w-full h-11 rounded-lg border border-slate-200 bg-white px-4 text-sm focus:outline-none focus:ring-2 focus:ring-[#3c68ff] focus:ring-offset-1"
              />
            </div>

            <button
              data-testid="reset-password-submit-btn"
              type="submit"
              disabled={isLoading}
              className="w-full h-11 bg-[#3c68ff] text-white rounded-lg text-sm font-semibold hover:bg-[#2b4bca] transition-all shadow-sm flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed mt-6"
            >
              {isLoading ? (
                <>
                  <Loader2 size={16} className="animate-spin" />
                  Resetting...
                </>
              ) : (
                "Reset Password"
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};
